// m1-audio-engine.js — audio playback, mute, crossfade, and preview.
// Exposes window.M1 = { play, stop, setVolume, toggleMute, onMuteRampTick,
//                       onMuteChange, startPreview, stopPreview, isPreviewActive,
//                       onHlsInstance, _state }.
// Metadata extraction is handled by M6 (Metadata Engine).
//
// Internal architecture: deterministic state machine for playback lifecycle.
// States: IDLE → LOADING → FADING_OUT → FADING_IN → PLAYING
//         + PREVIEW_LOAD → PREVIEWING → PREVIEW_STOPPING → NONE
// Volume/mute are orthogonal parameters applied on state entry.
// All volume operations use audio.volume directly (desktop-only — writable).

window.M1 = (() => {
  // ---------------------------------------------------------------------------
  // State machine
  // ---------------------------------------------------------------------------

  const STATE = {
    IDLE:             'IDLE',
    LOADING:          'LOADING',
    FADING_OUT:       'FADING_OUT',
    FADING_IN:        'FADING_IN',
    PLAYING:          'PLAYING',
    PREVIEW_LOAD:     'PREVIEW_LOAD',
    PREVIEWING:       'PREVIEWING',
    PREVIEW_STOPPING: 'PREVIEW_STOPPING',
  };

  let _state = STATE.IDLE;

  // ── Timing constants ─────────────────────────────────────────
  const FADE_STEPS = 15;
  const CROSSFADE_DURATION_MS = 500;
  const PREVIEW_PRIMARY_FADE_INTERVAL_MS = 33;
  const PREVIEW_STOP_FADE_MS = 2000;
  const STREAM_READY_TIMEOUT_MS = 3000;
  const MUTE_RATE_MS_PER_PCT = 7.5; // tuned by ear for natural mute ramp
  const STREAM_DEBOUNCE_MS = 200;
  const PREVIEW_CROSSFADE_STEPS = 8;
  const PREVIEW_CROSSFADE_INTERVAL_MS = 25; // ~200ms total fade

  function _transition(newState) {
    const old = _state;
    _exitState(old);
    _state = newState;
    if (old !== newState) {
      Logger.log({
        ts: Date.now(), module: 'M1', fn: '_transition',
        inputs: { from: old, to: newState }, output: newState, durationMs: 0, error: null,
      });
      // Fire onPlaying the first time audio actually begins playing, however
      // playback was started (first-gesture resumePlayback, next, prev, playStation).
      // Used by M5 to clear the pendingPlayback affordance (GLM Batch C — T2.2).
      if (newState === STATE.PLAYING && old !== STATE.PLAYING) {
        _fireOnPlaying();
      }
    }
  }

  function _fireOnPlaying() {
    if (_onPlayingCb) {
      try { _onPlayingCb(); } catch (e) {
        Logger.log({ ts: Date.now(), module: 'M1', fn: 'onPlaying_callback', inputs: {}, output: null, durationMs: 0, error: e });
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Module state (shared across states)
  // ---------------------------------------------------------------------------

  let _audio          = null;   // HTMLAudioElement — active element for current state
  let _nextElement    = null;   // HTMLAudioElement — next stream being prepared (LOADING/FADING_OUT)
  let _currentUrl     = null;   // URL currently loaded or loading

  // Volume / mute (orthogonal axis)
  let _volume             = 0.5;
  let _lastNonZeroVolume  = 0.5;
  let _savedVolume        = 0.5;
  let _muted              = false;

  // Mute ramp
  let _muteRampTimer    = null;
  let _rampCurrentVol   = null;
  let _muteRampTickCb   = null;
  let _onMuteChangeCb   = null;

  // Fade timers
  let _fadeTimer        = null;   // setInterval ID for active fade (out or in — only one at a time)

  // Loading management
  let _loadingAbortCtrl = null;

  // HLS callback
  let _onHlsInstanceCb  = null;

  // Preview state
  let _previewAudio      = null;
  let _primaryWasMuted   = false;
  let _previewFadeTimer  = null;
  let _primaryPreviewFadeTimer = null; // primary-audio fade on preview start (T2.3)
  let _previewUrl        = null;
  let _onPreviewChangeCb = null;
  let _onPlayingCb        = null;

  // iOS detection (audio.volume is read-only on iOS Safari)
  let _isIOS = null; // cached lazy
  function _detectIOS() {
    if (_isIOS !== null) return _isIOS;
    const ua = navigator.userAgent;
    _isIOS = /iPhone|iPod/.test(ua);
    return _isIOS;
  }

  // ---------------------------------------------------------------------------
  // Volume helpers (all via audio.volume — desktop writable)
  // ---------------------------------------------------------------------------

  function _setVol(audioEl, vol) {
    if (audioEl) audioEl.volume = vol;
  }

  function _getVol(audioEl) {
    return audioEl ? audioEl.volume : 0;
  }

  // ---------------------------------------------------------------------------
  // Element lifecycle
  // ---------------------------------------------------------------------------

  function _destroyAudio(a) {
    if (!a) return;
    if (a._hls) { a._hls.destroy(); a._hls = null; }
    a.pause();
    a.src = '';
  }

  /** Stop + destroy: force-silence then teardown. Used on state exit. */
  function _stopAndDestroy(a) {
    if (!a) return;
    a.volume = 0;
    _destroyAudio(a);
  }

  // ---------------------------------------------------------------------------
  // Mute ramp (orthogonal animation — not a state)
  // ---------------------------------------------------------------------------

  function _cancelMuteRamp() {
    if (_muteRampTimer !== null) {
      clearInterval(_muteRampTimer);
      _muteRampTimer = null;
    }
  }

  function _runMuteRamp(startVol, endVol, durationMs) {
    _cancelMuteRamp();
    _rampCurrentVol = startVol;
    if (durationMs <= 0) {
      if (_audio) _audio.volume = endVol;
      _rampCurrentVol = null;
      if (_muteRampTickCb) _muteRampTickCb(endVol);
      return;
    }
    const steps    = Math.max(1, Math.round(durationMs / 16));
    const interval = durationMs / steps;
    let   step     = 0;

    _muteRampTimer = setInterval(() => {
      step++;
      const t   = Math.min(1, step / steps);
      const vol = startVol + (endVol - startVol) * t;
      _rampCurrentVol = vol;
      if (_audio) _audio.volume = vol;
      if (_muteRampTickCb) _muteRampTickCb(vol);
      if (step >= steps) {
        clearInterval(_muteRampTimer);
        _muteRampTimer    = null;
        _rampCurrentVol = null;
      }
    }, interval);
  }

  // ---------------------------------------------------------------------------
  // Fade helpers
  // ---------------------------------------------------------------------------

  function _cancelFade() {
    if (_fadeTimer !== null) {
      clearInterval(_fadeTimer);
      _fadeTimer = null;
    }
  }

  function _doFadeOut(audioEl, durationMs, done) {
    const steps    = FADE_STEPS;
    const interval = durationMs / steps;
    const startVol = _getVol(audioEl);
    let   step     = 0;

    _fadeTimer = setInterval(() => {
      step++;
      const vol = Math.max(0, startVol * (1 - step / steps));
      _setVol(audioEl, vol);
      if (step >= steps) {
        clearInterval(_fadeTimer);
        _fadeTimer = null;
        done();
      }
    }, interval);
  }

  function _doFadeIn(audioEl, targetVol, durationMs, done) {
    const steps    = FADE_STEPS;
    const interval = durationMs / steps;
    let   step     = 0;

    _fadeTimer = setInterval(() => {
      step++;
      const vol = Math.min(targetVol, targetVol * (step / steps));
      _setVol(audioEl, vol);
      if (step >= steps) {
        clearInterval(_fadeTimer);
        _fadeTimer = null;
        _setVol(audioEl, targetVol);
        if (done) done();
      }
    }, interval);
  }

  // ---------------------------------------------------------------------------
  // State exit handlers — guarantee complete cleanup
  // ---------------------------------------------------------------------------

  function _exitState(s) {
    switch (s) {
      case STATE.LOADING:
        if (_loadingAbortCtrl) {
          _loadingAbortCtrl.abort();
          _loadingAbortCtrl = null;
        }
        if (_nextElement) {
          _stopAndDestroy(_nextElement);
          _nextElement = null;
        }
        break;

      case STATE.FADING_OUT:
        _cancelFade();
        if (_audio)  { _stopAndDestroy(_audio);  _audio = null; }
        if (_nextElement) { _stopAndDestroy(_nextElement); _nextElement = null; }
        break;

      case STATE.FADING_IN:
        _cancelFade();
        if (_audio) { _stopAndDestroy(_audio); _audio = null; }
        break;

      case STATE.PLAYING:
        break;

      case STATE.PREVIEWING:
      case STATE.PREVIEW_STOPPING:
        _cancelFade();
        if (_primaryPreviewFadeTimer) {
          clearInterval(_primaryPreviewFadeTimer);
          _primaryPreviewFadeTimer = null;
        }
        if (_previewFadeTimer) {
          clearInterval(_previewFadeTimer);
          _previewFadeTimer = null;
        }
        if (_previewAudio) {
          _stopAndDestroy(_previewAudio);
          _previewAudio = null;
        }
        _previewUrl = null;
        break;

      case STATE.PREVIEW_LOAD:
        if (_previewAudio) {
          _stopAndDestroy(_previewAudio);
          _previewAudio = null;
        }
        break;

      default:
        break;
    }
    _cancelMuteRamp();
  }

  // ---------------------------------------------------------------------------
  // HTML parsing helpers
  // ---------------------------------------------------------------------------

  function _isPlaylist(url) {
    return /\.(pls|m3u)(\?.*)?$/i.test(url);
  }

  function _isHls(url) {
    return /\.m3u8(\?|$)/i.test(url);
  }

  let _apiBase = null;

  async function _resolvePlaylist(url, signal) {
    try {
      // Use the configured API base when available (mirrors M6.init(apiBaseUrl)).
      // Under file:// (no backend), _apiBase is null and we fall back to a direct
      // fetch of the playlist URL. GLM Batch C — T2.4.
      if (_apiBase) {
        const apiUrl = `${_apiBase}/api/resolve?url=${encodeURIComponent(url)}`;
        const response = await fetch(apiUrl, { signal });
        if (response.ok) {
          const data = await response.json();
          if (data.streamUrl) return data.streamUrl;
        }
        // If the resolve endpoint failed, fall through to direct fetch below.
      }
      const directResponse = await fetch(url, { signal });
      if (!directResponse.ok) return null;
      const text = await directResponse.text();

      if (/\.pls(\?|$)/i.test(url)) {
        const match = text.match(/^File\d+=(.+)$/im);
        return match ? match[1].trim() : null;
      } else {
        const line = text.split(/\r?\n/).find(l => l.trim() && !l.startsWith('#'));
        return line ? line.trim() : null;
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        Logger.log({
          ts: Date.now(), module: 'M1', fn: '_resolvePlaylist',
          inputs: { url }, output: null, durationMs: 0, error: e,
        });
      }
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Stream loading (used by LOADING state entry)
  // ---------------------------------------------------------------------------

  function _beginLoad(url) {
    _currentUrl = url;
    _loadingAbortCtrl = new AbortController();
    const loadCtrl = _loadingAbortCtrl;

    async function launch(streamUrl) {
      if (loadCtrl.signal.aborted) return;

      if (_isHls(streamUrl)) {
        if (typeof Hls === 'undefined') {
          Logger.log({ ts: Date.now(), module: 'M1', fn: '_beginLoad', inputs: { url }, output: null, durationMs: 0, error: new Error('HLS.js not available') });
          _transition(STATE.IDLE);
          return;
        }
        if (loadCtrl.signal.aborted) return;
      }

      const a = new Audio();
      a.volume = 0;
      a.addEventListener('timeupdate', _onTimeupdate);
      _nextElement = a;

      // Always wait for the stream to be ready before calling _onStreamReady.
      if (_isHls(streamUrl) && typeof Hls !== 'undefined' && Hls.isSupported()) {
        const hls = new Hls();
        a._hls = hls;
        hls.loadSource(streamUrl);
        hls.attachMedia(a);
        if (_onHlsInstanceCb) _onHlsInstanceCb(hls);

        let _readyFired = false;
        const cleanup = () => {
          a.removeEventListener('canplay', onReady);
          hls.off(Hls.Events.ERROR, onHlsError);
          clearTimeout(_readyTimeout);
        };
        const proceed = () => { if (_readyFired) return; _readyFired = true; cleanup(); _onStreamReady(a, streamUrl, loadCtrl); };
        const onReady    = () => proceed();
        const onHlsError = (_, data) => { if (data.fatal) proceed(); };
        const _readyTimeout = setTimeout(proceed, STREAM_READY_TIMEOUT_MS);
        a.addEventListener('canplay', onReady);
        hls.on(Hls.Events.ERROR, onHlsError);
      } else {
        a.src = streamUrl;

        let _readyFired = false;
        const cleanup = () => {
          a.removeEventListener('canplay', onReady);
          a.removeEventListener('loadeddata', onReady);
          a.removeEventListener('error', onReady);
          clearTimeout(_readyTimeout);
        };
        const proceed = () => { if (_readyFired) return; _readyFired = true; cleanup(); _onStreamReady(a, streamUrl, loadCtrl); };
        const onReady = () => proceed();
        const _readyTimeout = setTimeout(proceed, STREAM_READY_TIMEOUT_MS);
        a.addEventListener('canplay', onReady);
        a.addEventListener('loadeddata', onReady);
        a.addEventListener('error', onReady);
      }
    }

    if (_isPlaylist(url)) {
      _resolvePlaylist(url, loadCtrl.signal).then(streamUrl => {
        if (!streamUrl || loadCtrl.signal.aborted) return;
        launch(streamUrl);
      });
    } else {
      launch(url);
    }
  }

  /** Called when the new stream is ready to play (canplay or equivalent). */
  function _onStreamReady(a, streamUrl, loadCtrl) {
    if (loadCtrl.signal.aborted) {
      _stopAndDestroy(a);
      return;
    }
    _loadingAbortCtrl = null;
    _nextElement = a;

    const hasPrevious = _audio && !_audio.paused;

    // Register persistent error recovery on the audio element.
    let _errorRetries = 0;
    function _attachErrorRecovery(el, url) {
      const onStreamError = () => {
        const code = el.error ? el.error.code : 'unknown';
        Logger.log({ ts: Date.now(), module: 'M1', fn: 'stream_error', inputs: { url, errorCode: code, retry: _errorRetries }, output: null, durationMs: 0, error: null });
        // Icecast/SHOUTcast streams may trigger transient SRC_NOT_SUPPORTED (code 4)
        // during initial buffering. If audio is actually playing despite the error,
        // don't disrupt it — the stream recovered on its own.
        if (code === 4 && !el.paused && el.readyState > 0) return;
        if (_state !== STATE.PLAYING || _audio !== el) return;
        if (_errorRetries === 0) {
          _errorRetries++;
          Logger.log({ ts: Date.now(), module: 'M1', fn: 'stream_error_retry', inputs: { url }, output: null, durationMs: 0, error: null });
          if (_detectIOS()) {
            // iOS: keep audio element alive (paused, src cleared) to preserve
            // Media Session. Destroying it would cause iOS to deregister and
            // fall through to other audio sources (e.g. Spotify in another tab).
            el.pause();
            el.removeAttribute('src');
          } else {
            _stopAndDestroy(el);
          }
          _audio = null;
          _beginLoad(url);
        } else {
          Logger.log({ ts: Date.now(), module: 'M1', fn: 'stream_error_gave_up', inputs: { url }, output: null, durationMs: 0, error: null });
          if (_detectIOS()) {
            // iOS: keep audio element alive (paused, muted) to preserve Media Session.
            // iOS deregisters the session when the audio element is destroyed,
            // causing fallthrough to other audio sources (e.g. Spotify in another tab).
            el.pause();
            el.removeAttribute('src');
          } else {
            _stopAndDestroy(el);
          }
          _audio = null;
          _transition(STATE.IDLE);
        }
      };
      el.addEventListener('error', onStreamError);
      el.addEventListener('ended', () => {
        Logger.log({ ts: Date.now(), module: 'M1', fn: 'stream_ended', inputs: { url }, output: null, durationMs: 0, error: null });
        if (_state === STATE.PLAYING && _audio === el) {
          if (_detectIOS()) {
            // iOS: keep audio element alive to preserve Media Session
            el.pause();
          } else {
            _transition(STATE.IDLE);
          }
        }
      });
    }

    if (hasPrevious) {
      if (_detectIOS()) {
        // iOS: audio.volume is read-only — crossfade doesn't work.
        // Start new stream, pause the old one. Do NOT destroy the old audio
        // element — if it's destroyed before the new one is playing, iOS
        // deregisters our Media Session and falls through to other sources.
        _nextElement = null;
        a.volume = 1; // ignored on iOS, but set as intent

        // Pause old audio FIRST, before starting the new one.
        // If we call play() then pause(), the pause overwrites the play
        // in iOS MediaSession, showing ▶ (play) instead of ❚❚ (pause).
        if (_audio) {
          _audio.pause();
          _audio.removeAttribute('src');
        }

        _audio = a;
        _nextElement = null;

        // Start new audio — this sets playbackState to 'playing' as the
        // LAST Media Session event, so the lock screen shows ❚❚.
        a.play().catch(e => {
          Logger.log({
            ts: Date.now(), module: 'M1', fn: 'play_audio_error_ios',
            inputs: { url: streamUrl }, output: null, durationMs: 0, error: e,
          });
        });

        _transition(STATE.PLAYING);
        _attachErrorRecovery(_audio, streamUrl);
      } else {
        // Transition to FADING_OUT — crossfade (non-iOS, volume writable).
        _nextElement = null;
        _transition(STATE.FADING_OUT);
        _nextElement = a;
        a.volume = 0;
        a.play().catch(e => {
          Logger.log({
            ts: Date.now(), module: 'M1', fn: 'play_audio_error',
            inputs: { url: streamUrl }, output: null, durationMs: 0, error: e,
          });
        });

        // Crossfade: fade out old, destroy old, then fade in new.
        _doFadeOut(_audio, CROSSFADE_DURATION_MS, () => {
          _stopAndDestroy(_audio);
          _nextElement = null;
          _transition(STATE.FADING_IN);
          _audio = a;
          const target = _muted ? 0 : _volume;
          _doFadeIn(_audio, target, CROSSFADE_DURATION_MS, () => {
            const el = _audio;
            _audio = null;
            _transition(STATE.PLAYING);
            _audio = el;
            _attachErrorRecovery(_audio, streamUrl);
          });
        });
      }
    } else {
      // No previous audio — go straight to PLAYING
      _stopAndDestroy(_audio);
      _audio = a;
      _nextElement = null;

      _transition(STATE.PLAYING);
      const target = _muted ? 0 : _volume;
      _audio.volume = target;
      _attachErrorRecovery(_audio, streamUrl);

      let _playAttempts = 0;
      const _tryPlay = () => {
        _playAttempts++;
        _audio.play().then(() => {
          Logger.log({ ts: Date.now(), module: 'M1', fn: 'play_started', inputs: { url: streamUrl, attempt: _playAttempts }, output: null, durationMs: 0, error: null });
        }).catch(e => {
          Logger.log({
            ts: Date.now(), module: 'M1', fn: 'play_audio_error',
            inputs: { url: streamUrl, attempt: _playAttempts }, output: null, durationMs: 0, error: e,
          });
          if (_state !== STATE.PLAYING || _audio !== a) return;
          if (_playAttempts < 3) {
            const delay = _playAttempts === 1 ? 1000 : STREAM_READY_TIMEOUT_MS;
            setTimeout(() => {
              if (_state === STATE.PLAYING && _audio === a) {
                _stopAndDestroy(a);
                _audio = null;
                _beginLoad(streamUrl);
              }
            }, delay);
          } else {
            Logger.log({ ts: Date.now(), module: 'M1', fn: 'play_gave_up', inputs: { url: streamUrl, attempts: _playAttempts }, output: null, durationMs: 0, error: e });
            _stopAndDestroy(a);
            _audio = null;
            _transition(STATE.IDLE);
          }
        });
      };
      _tryPlay();
    }
  }

  // ---------------------------------------------------------------------------
  // Public interface
  // ---------------------------------------------------------------------------

  const play = Logger.traced('M1', 'play', (url) => {
    if (_state === STATE.PREVIEWING || _state === STATE.PREVIEW_STOPPING || _state === STATE.PREVIEW_LOAD) {
      _exitState(_state);
      _state = STATE.IDLE;
    }

    const prevAudio = _audio;
    _transition(STATE.LOADING);

    if (prevAudio && !prevAudio.paused) {
      _audio = prevAudio;
    }

    _beginLoad(url);
    return true;
  });

  const stop = Logger.traced('M1', 'stop', () => {
    if (_audio) {
      _stopAndDestroy(_audio);
      _audio = null;
    }
    _transition(STATE.IDLE);
    _audio = null;
    _nextElement = null;
    _currentUrl = null;
    return true;
  });

  const setVolume = Logger.traced('M1', 'setVolume', (level) => {
    const clamped = Math.max(0, Math.min(1, level));

    _cancelMuteRamp();
    _rampCurrentVol = null;

    _volume = clamped;
    if (clamped > 0) _lastNonZeroVolume = clamped;

    let targetVol = null;
    if (clamped === 0 && !_muted) {
      _savedVolume = _lastNonZeroVolume;
      _muted = true;
      targetVol = 0;
      if (_onMuteChangeCb) _onMuteChangeCb({ muted: true, volume: 0 });
    } else if (clamped > 0 && _muted) {
      _muted = false;
      targetVol = clamped;
      if (_onMuteChangeCb) _onMuteChangeCb({ muted: false, volume: clamped });
    } else if (!_muted) {
      targetVol = clamped;
    }

    if (targetVol !== null && _audio) {
      _audio.volume = targetVol;
    }

    return clamped;
  }, { level: 'debug' });

  const toggleMute = Logger.traced('M1', 'toggleMute', () => {
    // iOS: audio.volume is read-only — use pause/play instead of ramp.
    if (_detectIOS()) {
      _cancelMuteRamp();
      _rampCurrentVol = null;

      if (!_muted) {
        _savedVolume = _volume > 0 ? _volume : (_lastNonZeroVolume || 0.5);
        _muted = true;
        if (_audio) {
          _audio.pause();
        }
        const result = { muted: true, volume: 0 };
        if (_onMuteChangeCb) _onMuteChangeCb(result);
        return result;
      } else {
        _muted = false;
        _volume = _savedVolume || 0.5;
        if (_audio) {
          _audio.play().catch(e => {
            Logger.log({
              ts: Date.now(), module: 'M1', fn: 'toggleMute_ios_play',
              inputs: {}, output: null, durationMs: 0, error: e,
            });
          });
        }
        const result = { muted: false, volume: _volume };
        if (_onMuteChangeCb) _onMuteChangeCb(result);
        return result;
      }
    }

    const currentVol = _rampCurrentVol !== null ? _rampCurrentVol
                     : _audio                   ? _audio.volume
                     : _muted                   ? 0
                     :                            _volume;

    _cancelMuteRamp();
    _rampCurrentVol = null;

    let result;
    if (!_muted) {
      _savedVolume = _volume > 0 ? _volume : (_lastNonZeroVolume || 0.5);
      _muted       = true;
      const durationMs = currentVol * 100 * MUTE_RATE_MS_PER_PCT;
      _runMuteRamp(currentVol, 0, durationMs);
      result = { muted: true, volume: 0 };
    } else {
      _muted  = false;
      _volume = _savedVolume || 0.5;
      const durationMs = (_volume - currentVol) * 100 * MUTE_RATE_MS_PER_PCT;
      _runMuteRamp(currentVol, _volume, durationMs);
      result = { muted: false, volume: _volume };
    }

    if (_onMuteChangeCb) _onMuteChangeCb(result);

    // If in FADING_IN state, toggleMute cancels the fade-in and applies immediately.
    if (_state === STATE.FADING_IN) {
      _cancelFade();
      if (_audio) _audio.volume = _muted ? 0 : _volume;
      const el = _audio;
      _audio = null;
      _transition(STATE.PLAYING);
      _audio = el;
    }

    return result;
  });

  const onMuteRampTick = Logger.traced('M1', 'onMuteRampTick', (callback) => {
    _muteRampTickCb = callback;
    return true;
  }, { level: 'debug' });

  const onMuteChange = Logger.traced('M1', 'onMuteChange', (callback) => {
    _onMuteChangeCb = callback;
    return true;
  });

  const onHlsInstance = Logger.traced('M1', 'onHlsInstance', (callback) => {
    _onHlsInstanceCb = callback;
    return true;
  });

  const onPlaying = Logger.traced('M1', 'onPlaying', (callback) => {
    _onPlayingCb = callback;
    return true;
  });

  // Configuration: backend API base for playlist resolution (mirrors M6.init).
  // null under file:// -> _resolvePlaylist falls back to direct fetch.
  function init(opts) {
    if (opts && typeof opts === 'object' && 'apiBase' in opts) {
      _apiBase = opts.apiBase || null;
    }
    return true;
  }
  function setApiBase(url) {
    _apiBase = url || null;
    return true;
  }

  // ---------------------------------------------------------------------------
  // Preview playback
  // ---------------------------------------------------------------------------

  const startPreview = Logger.tracedAsync('M1', 'startPreview', async (url) => {
    Logger.log({ ts: Date.now(), module: 'M1', fn: 'startPreview', inputs: { url, state: _state, hasOldPreview: !!_previewAudio }, output: null, durationMs: 0, level: 'debug', error: null });
    if (_previewFadeTimer !== null) {
      clearInterval(_previewFadeTimer);
      _previewFadeTimer = null;
    }
    if (_previewAudio) {
      // Fade out old preview before destroying
      const oldPreview = _previewAudio;
      const oldVol = oldPreview.volume;
      if (oldVol > 0) {
        const steps = PREVIEW_CROSSFADE_STEPS;
        await new Promise(resolve => {
          let step = 0;
          const tick = setInterval(() => {
            step++;
            oldPreview.volume = Math.max(0, oldVol * (1 - step / steps));
            if (step >= steps) { clearInterval(tick); resolve(); }
          }, PREVIEW_CROSSFADE_INTERVAL_MS);
        });
      }
      _stopAndDestroy(oldPreview);
      _previewAudio = null;
      _previewUrl = null;
      Logger.log({ ts: Date.now(), module: 'M1', fn: 'startPreview_oldDestroyed', inputs: {}, output: 'onPreviewChange false', durationMs: 0, level: 'debug', error: null });
      if (_onPreviewChangeCb) _onPreviewChangeCb({ active: false, url: null });
    }

    _primaryWasMuted = _muted;

    // Cancel any previous primary fade (e.g. rapid start→stop→start). T2.3.
    if (_primaryPreviewFadeTimer) {
      clearInterval(_primaryPreviewFadeTimer);
      _primaryPreviewFadeTimer = null;
    }

    // Fade main audio over 500ms (not abrupt cut). Tracked on a module-level
    // timer so _exitState(PREVIEW_*) and stopPreview can cancel it — no orphaned
    // interval writes _audio.volume after a stop/state-exit. GLM Batch C — T2.3.
    if (_audio) {
      const startVol = _audio.volume;
      const steps = 15;
      const interval = PREVIEW_PRIMARY_FADE_INTERVAL_MS; // ~500ms total
      let step = 0;
      _primaryPreviewFadeTimer = setInterval(() => {
        step++;
        _audio.volume = Math.max(0, startVol * (1 - step / steps));
        if (step >= steps) {
          clearInterval(_primaryPreviewFadeTimer);
          _primaryPreviewFadeTimer = null;
        }
      }, interval);
    }

    _transition(STATE.PREVIEW_LOAD);

    const preview = new Audio();
    preview.volume = 0;
    preview.addEventListener('timeupdate', _onTimeupdate);
    _previewAudio = preview;
    _previewUrl = url;
    // Signal immediately so button changes at click time, before audio loads
    Logger.log({ ts: Date.now(), module: 'M1', fn: 'startPreview_loading', inputs: { url }, output: 'onPreviewChange true', durationMs: 0, level: 'debug', error: null });
    if (_onPreviewChangeCb) _onPreviewChangeCb({ active: true, url });

    try {
      if (_isHls(url)) {
        if (typeof Hls !== 'undefined' && Hls.isSupported()) {
          const hls = new Hls();
          preview._hls = hls;
          hls.loadSource(url);
          hls.attachMedia(preview);
        } else {
          preview.src = url;
        }
      } else if (_isPlaylist(url)) {
        const streamUrl = await _resolvePlaylist(url, new AbortController().signal);
        if (!streamUrl) {
          _transition(STATE.IDLE);
          return { success: false };
        }
        preview.src = streamUrl;
      } else {
        preview.src = url;
      }

      const previewVol = _primaryWasMuted ? _savedVolume || 0.5 : _volume || 0.5;
      preview.volume = previewVol;

      // Wait for canplay — Safari may resolve play() before data is ready
      await new Promise((resolve) => {
        const _onReady = () => {
          preview.removeEventListener('canplay', _onReady);
          preview.removeEventListener('loadeddata', _onReady);
          preview.removeEventListener('error', _onReady);
          clearTimeout(_readyTo);
          resolve();
        };
        const _readyTo = setTimeout(_onReady, STREAM_READY_TIMEOUT_MS);
        preview.addEventListener('canplay', _onReady);
        preview.addEventListener('loadeddata', _onReady);
        preview.addEventListener('error', _onReady);
      });

      // Check element wasn't destroyed during canplay wait (rapid stop/start)
      if (_previewAudio !== preview) return { success: false };

      await preview.play();
      // Debug: confirm volume and playing state
      Logger.log({ ts: Date.now(), module: 'M1', fn: 'startPreview_playing', inputs: { volume: preview.volume, paused: preview.paused, readyState: preview.readyState }, output: null, durationMs: 0, error: null });
      // Clear ref before transition — _exitState(PREVIEW_LOAD) destroys _previewAudio
      // (designed for ABORT). Natural completion: same pattern as FADING transitions.
      _previewAudio = null;
      _transition(STATE.PREVIEWING);
      _previewAudio = preview;
      Logger.log({ ts: Date.now(), module: 'M1', fn: 'startPreview_success', inputs: { url }, output: true, durationMs: 0, level: 'debug', error: null });
      return { success: true };
    } catch (e) {
      Logger.log({ ts: Date.now(), module: 'M1', fn: 'startPreview_failed', inputs: { url }, output: null, durationMs: 0, level: 'debug', error: e });
      _stopAndDestroy(preview);
      _previewAudio = null;
      _previewUrl = null;
      if (_onPreviewChangeCb) _onPreviewChangeCb({ active: false, url: null });
      if (_audio && !_primaryWasMuted) {
        _audio.volume = _volume;
      }
      _transition(STATE.IDLE);
      return { success: false };
    }
  });

  const stopPreview = Logger.traced('M1', 'stopPreview', () => {
    // Cancel any in-progress primary fade so it can't tug _audio.volume while
    // we restore primary volume below. GLM Batch C — T2.3.
    if (_primaryPreviewFadeTimer) {
      clearInterval(_primaryPreviewFadeTimer);
      _primaryPreviewFadeTimer = null;
    }
    if (!_previewAudio) {
      if (_state === STATE.PREVIEWING || _state === STATE.PREVIEW_STOPPING) {
        _previewUrl = null;
        if (_onPreviewChangeCb) _onPreviewChangeCb({ active: false, url: null });
        _transition(STATE.IDLE);
      }
      return { resumed: !!_audio };
    }

    const preview   = _previewAudio;
    const wasMuted  = _primaryWasMuted;

    _transition(STATE.PREVIEW_STOPPING);

    function _restorePrimary() {
      if (_audio && !wasMuted) {
        _audio.volume = _muted ? 0 : _volume;
      }
      _previewUrl = null;
      if (_onPreviewChangeCb) _onPreviewChangeCb({ active: false, url: null });
      _transition(STATE.IDLE);
    }

    const steps     = 30;
    const interval  = PREVIEW_STOP_FADE_MS / steps;
    const startVol  = preview.volume;
    let   step      = 0;

    if (startVol <= 0) {
      _stopAndDestroy(preview);
      _previewAudio = null;
      _restorePrimary();
      return { resumed: !!_audio };
    }

    _previewFadeTimer = setInterval(() => {
      step++;
      const vol = Math.max(0, startVol * (1 - step / steps));
      preview.volume = vol;
      if (step >= steps) {
        clearInterval(_previewFadeTimer);
        _previewFadeTimer = null;
        _stopAndDestroy(preview);
        if (_previewAudio === preview) _previewAudio = null;
        _restorePrimary();
      }
    }, interval);

    return { resumed: !!_audio };
  });

  const isPreviewActive = Logger.traced('M1', 'isPreviewActive', () => {
    return _state === STATE.PREVIEWING || _state === STATE.PREVIEW_STOPPING || _state === STATE.PREVIEW_LOAD;
  });

  const onPreviewChange = Logger.traced('M1', 'onPreviewChange', (callback) => {
    _onPreviewChangeCb = callback;
    return true;
  });

  // Expose state for debugging (M1-054) and _muted for M5 mount
  
  let _heartbeatCb = null;
  let _heartbeatEl = null;
  
  function _onTimeupdate() {
    if (_heartbeatCb) {
      try { _heartbeatCb(); } catch (e) { /* best-effort */ }
    }
  }
  
  return {
    play, stop, setVolume, toggleMute, onMuteRampTick, onMuteChange,
    startPreview, stopPreview, isPreviewActive, onHlsInstance,
    onPreviewChange, onPlaying, init, setApiBase,
    get _state() { return _state; },
    get _muted() { return _muted; },
    isIOS: _detectIOS,
    /** iOS-only: synchronous pause→play cycle to activate Media Session. No-op on other platforms.
     * Returns { success: boolean } — true when the cycle executed (audio element existed and is iOS). */
    wakeMediaSession: function () {
      if (!_detectIOS() || !_audio) return { success: false };
      _audio.pause();
      _audio.play().catch(function () {});
      return { success: true };
    },
    /**
     * Register a callback that fires on every audio timeupdate event.
     * Used by M6 as a background-safe polling heartbeat — on iOS, the audio
     * element continues playing and firing timeupdate even when the screen
     * is locked, unlike setInterval which gets throttled/suspended.
     * Returns an unsubscribe function.
     */
    onHeartbeat: function (cb) {
      _heartbeatCb = cb;
      return function () { _heartbeatCb = null; };
    },
  };
})();
